/**
 * this component will display the Subscription Terms information
 *
 * @module views/components/SubscriptionInfo
 * @memberof -Common
 */
import './SubscriptionInfo.scss';

import React, { useEffect } from 'react';

import PropTypes from 'prop-types';

import Icon from '@ulta/core/components/Icon/Icon';
import Link_Huge from '@ulta/core/components/Link_Huge/Link_Huge';
import Text from '@ulta/core/components/Text/Text';
import { useDeviceInflection } from '@ulta/core/providers/InflectionProvider/InflectionProvider';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';

import { getSubsTermsProps } from '@ulta/utils/subscriptionUtil/subscriptionUtil';

import * as utils from './SubscriptionInfo';

/**
 * Represents a SubscriptionInfo component
 *
 * @method
 * @param {SubscriptionInfoProps} props - React properties passed from composition
 * @returns SubscriptionInfo
 */
export const SubscriptionInfo = function( props ){
  const {
    title,
    subscriptionBenefits,
    subscriptionTerms,
    closeAction,
    invokeAction } = props;

  const { breakpoint } = useDeviceInflection();
  const isMobile = breakpoint?.CURRENT_BREAKPOINT === 'SM';
  const getSubsTermsPropUtil = getSubsTermsProps( { isMobile : isMobile } );
  const { updateCloseAction } = useOverlay();

  useEffect( () => {
    utils.handleBackgroundUpdate( { closeAction }, { updateCloseAction, invokeAction } );
  }, [closeAction] );

  return (
    <div className='SubscriptionInfo'
      data-testid='subscription-info'
    >
      { !isMobile &&
      <div className='SubscriptionInfo__Header'>
        <Text
          textStyle='title-lg'
          htmlTag='span'
        >
          { title }
        </Text>
      </div>
      }

      {
        subscriptionBenefits && subscriptionBenefits.map( ( eachBenefit ) => {
          return (
            <div key={ eachBenefit?.benefitHeader }
              className='SubscriptionInfo__Benefits'
            >
              <div className='SubscriptionInfo__BenefitHeaderIcon'>
                <Icon
                  size='xl'
                  name={ eachBenefit.benefitIcon }
                  aria-hidden={ true }
                />
              </div>
              <div className='SubscriptionInfo__BenefitHeaderDetails'>
                <div className='SubscriptionInfo__BenefitHeaderDetailsRow' >
                  <Text
                    textStyle='body-lg'
                    htmlTag='span'
                  >
                    { eachBenefit.benefitHeader }
                  </Text>
                </div>
                <div className='SubscriptionInfo__BenefitHeaderDetailsRow' >
                  <Text
                    textStyle='body'
                    color='neutral-500'
                    htmlTag='span'
                  >
                    { eachBenefit.benefitDescription }
                  </Text>
                </div>
              </div>
            </div>
          );
        } )
      }
      { subscriptionTerms &&
        <div className='SubscriptionInfo__SubscriptionTerms'>
          <Link_Huge
            compact
            target={ getSubsTermsPropUtil.linkTarget }
            action={ subscriptionTerms }
            url={ subscriptionTerms.url }
            title={ subscriptionTerms.label }
          >
            { subscriptionTerms.label }
          </Link_Huge>
        </div>
      }
    </div>
  );
};

/**
 * handleBackgroundUpdate function - to set custom action to trigger while closing the overlay
 * @method
 * @param {Object} data - Passing required data as an argument
 * @param {Object} methods - Passing setstate methods as an argument
 */
export const handleBackgroundUpdate = ( data, methods ) => {
  const { closeAction } = data || {};
  const { updateCloseAction, invokeAction } = methods || {};
  if( !closeAction || !updateCloseAction || !invokeAction ){
    return;
  }
  updateCloseAction( { action: closeAction }, { invokeAction } );
};

/**
* Property type definitions
  * @typedef SubscriptionInfo
  * @type {object}
  * @property {string} title - Set the checkbox and Label and help text
  * @property {array} subscriptionBenefits - Sets value for an icon
  * @property {object} subscriptionTerms - Sets the list price to display
  * @property {object} closeAction - Sets the closeAction properties which triggers on closing the OverlayContainer
  * @property {func} invokeAction - for triggering graphql calls
*/
export const propTypes =  {
  title: PropTypes.string,
  subscriptionBenefits: PropTypes.array,
  subscriptionTerms: PropTypes.shape( {
    url: PropTypes.string,
    label: PropTypes.string
  } ),
  closeAction: PropTypes.object,
  invokeAction: PropTypes.func
};

SubscriptionInfo.propTypes = propTypes;

export default SubscriptionInfo;